import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IToastAction } from '@feathr/components';
import type { IDateRangeOption, useExportModal } from '@feathr/extender/hooks/useExportModal';

import type { IGroupExportTabHook, ISharedFormSlice } from '../GroupExportModal.types';
import type { IEmailActivityExportPayload, TEmailInteractionType } from '../GroupExportModal.utils';
import { DEFAULT_EMAIL_INTERACTION_TYPES } from '../GroupExportModal.utils';

export interface IDateRangeSection {
  selectedDateRange: string;
  customDateRange: [string | undefined, string | undefined];
  dateRangeHadFocus: boolean;
  dateRangeOptions: IDateRangeOption[];
  dateRangeError: string[] | undefined;
  handleDateRangeChange: (option: IDateRangeOption) => void;
  handleCustomDateRangeChange: (value: [string | undefined, string | undefined]) => void;
}

export interface IInteractionTypesSection {
  selectedInteractionTypes: TEmailInteractionType[];
  interactionTypesError: string[] | undefined;
  handleToggleInteractionType: (type: TEmailInteractionType) => void;
}

export interface IEmailActivityTabState
  extends ISharedFormSlice,
    IDateRangeSection,
    IInteractionTypesSection {}

export interface IUseEmailActivityTabOptions {
  form: ISharedFormSlice;
  dateRange: IDateRangeSection;
  resolveDateRange: ReturnType<typeof useExportModal>['resolveDateRange'];
  handleExport: ReturnType<typeof useExportModal>['handleExport'];
  /** Undefined disables submit (orchestrator gate). */
  onExportEmailActivity?: (payload: IEmailActivityExportPayload) => Promise<void>;
  successToastAction?: IToastAction;
}

export type IUseEmailActivityTabReturn = IGroupExportTabHook<IEmailActivityTabState>;

export function useEmailActivityTab({
  form,
  dateRange,
  resolveDateRange,
  handleExport,
  onExportEmailActivity,
  successToastAction,
}: IUseEmailActivityTabOptions): IUseEmailActivityTabReturn {
  const { t } = useTranslation();

  const [selectedInteractionTypes, setSelectedInteractionTypes] = useState<TEmailInteractionType[]>(
    [...DEFAULT_EMAIL_INTERACTION_TYPES],
  );

  const interactionTypesError = useMemo<string[] | undefined>(
    () =>
      selectedInteractionTypes.length === 0
        ? [t('Select at least one interaction type.')]
        : undefined,
    [selectedInteractionTypes.length, t],
  );

  const handleToggleInteractionType = useCallback((type: TEmailInteractionType): void => {
    setSelectedInteractionTypes((prev) =>
      prev.includes(type) ? prev.filter((existing) => existing !== type) : [...prev, type],
    );
  }, []);

  const validationErrors = useMemo<string[]>(() => {
    const errors: string[] = [];
    if (form.emailError) {
      errors.push(...form.emailError);
    }
    if (form.exportNameError) {
      errors.push(...form.exportNameError);
    }
    if (dateRange.dateRangeError) {
      errors.push(...dateRange.dateRangeError);
    }
    if (interactionTypesError) {
      errors.push(...interactionTypesError);
    }
    return errors;
  }, [form.emailError, form.exportNameError, dateRange.dateRangeError, interactionTypesError]);

  const handleSubmit = useCallback(async (): Promise<boolean> => {
    if (onExportEmailActivity === undefined) {
      return false;
    }
    const { startDate, endDate } = resolveDateRange();
    const payload: IEmailActivityExportPayload = {
      name: form.exportName,
      email: form.email,
      interaction_types: selectedInteractionTypes,
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
    };
    let didSucceed = false;
    await handleExport(
      () => onExportEmailActivity(payload),
      () => {
        didSucceed = true;
      },
      t,
      {
        successMessage: t('Your email activity export has started.'),
        successToastAction,
      },
    );
    return didSucceed;
  }, [
    form.email,
    form.exportName,
    handleExport,
    onExportEmailActivity,
    resolveDateRange,
    selectedInteractionTypes,
    successToastAction,
    t,
  ]);

  const resetState = useCallback((): void => {
    setSelectedInteractionTypes([...DEFAULT_EMAIL_INTERACTION_TYPES]);
  }, []);

  return {
    state: {
      ...form,
      ...dateRange,
      selectedInteractionTypes,
      interactionTypesError,
      handleToggleInteractionType,
    },
    validationErrors,
    handleSubmit,
    resetState,
  };
}
